import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import ModalComponent from '../../common/ModalComponent';
import { Mail } from 'lucide-react';
import { toast } from 'react-toastify';

const CREATE_LAB_COLLABORATOR = gql`
  mutation CreateLabCollaborator($laboratoryId: ID!, $email: String!, $firstName: String, $lastName: String, $tel: String) {
    createLabCollaborator(laboratoryId: $laboratoryId, email: $email, firstName: $firstName, lastName: $lastName, tel: $tel) {
      id
      nom
    }
  }
`;

const UserForm = ({ laboratory, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', tel: '' });
  const [formErrors, setFormErrors] = useState({});

  const [createLabCollaborator, { loading }] = useMutation(CREATE_LAB_COLLABORATOR, {
    onCompleted: () => {
      toast.success('Invitation envoyée — l\'utilisateur recevra un email pour activer son compte');
      setFormData({ email: '', firstName: '', lastName: '', tel: '' });
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Erreur lors de la création de l\'utilisateur');
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (value && formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.firstName) errors.firstName = 'Le prénom est requis';
    if (!formData.lastName) errors.lastName = 'Le nom est requis';
    if (!formData.email) errors.email = 'L\'email est requis';
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      errors.email = 'Format d\'email invalide';
    if (formData.tel && !/^(\+212|0)[5-7][0-9]{8}$/.test(formData.tel))
      errors.tel = 'Format de téléphone invalide (ex: +212 5XX XXX XXX)';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    createLabCollaborator({
      variables: {
        laboratoryId: laboratory.id,
        email: formData.email,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        tel: formData.tel || undefined,
      },
    });
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`Ajouter un collaborateur à ${laboratory?.nom || ''}`}
      onSubmit={handleSubmit}
      isSubmitting={loading}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
            />
            {formErrors.firstName && <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
            />
            {formErrors.lastName && <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
          />
          {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
          <input
            type="tel"
            name="tel"
            value={formData.tel}
            onChange={handleInputChange}
            placeholder="+212 5XX XXX XXX"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.tel ? 'border-red-500' : 'border-gray-300'}`}
          />
          {formErrors.tel && <p className="mt-1 text-sm text-red-600">{formErrors.tel}</p>}
        </div>

        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Mail className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-700">
              <p className="font-medium mb-1">Un email d'activation sera envoyé</p>
              <p>L'utilisateur recevra un lien pour définir son propre mot de passe et activer son compte sur le laboratoire <strong>{laboratory?.nom}</strong>.</p>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

export default UserForm;
